import React from 'react'

/** Parse consecutive markdown table lines into rows of cell strings. Skips empty cells from leading/trailing pipes. */
export function parseTableLines(lines: string[]): string[][] {
  const rows: string[][] = []
  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed || !trimmed.startsWith('|')) break
    const cells = trimmed.split('|').filter((c) => c.trim())
    rows.push(cells)
  }
  return rows
}

interface MarkdownTableProps {
  lines: string[]
}

/**
 * Render a markdown table (auto-detects the `---|---` header separator row).
 * Shared by blog articles and FAQ answers.
 */
const MarkdownTable: React.FC<MarkdownTableProps> = ({ lines }) => {
  const rows = parseTableLines(lines)
  if (rows.length < 2) return null
  const headerCells = rows[0]
  const isSeparator = rows[1].every((c) => /^[-:\s]+$/.test(c))
  const dataRows = isSeparator ? rows.slice(2) : rows.slice(1)

  return (
    <div className="overflow-x-auto my-6 rounded-xl border border-border shadow-sm">
      <table className="min-w-full border-collapse text-sm sm:text-base">
        <thead>
          <tr className="bg-gradient-to-r from-primary-600/10 to-secondary-600/10">
            {headerCells.map((cell, ci) => (
              <th
                key={ci}
                className="border-b border-border px-4 py-3 text-left font-bold text-heading whitespace-nowrap first:rounded-tl-xl last:rounded-tr-xl"
              >
                {cell.replace(/\*\*/g, '')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? 'bg-card' : 'bg-muted/30'}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className={`border-b border-border px-4 py-3 text-text-primary/90 ${
                    ci === 0 ? 'font-semibold text-heading whitespace-nowrap' : ''
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default MarkdownTable
